import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AmmDataLabelValue, AmmIconCategory } from '../../components.global';
import {
  CountryNameMap,
  CountryStatesMap,
  PagingData,
  UseraccountModel,
} from '../models';
import { Address, CityStateZip, State } from '../models/address.model';
import { GlobaldataService, ProductService, ShoppingCartService } from '../services';
import { CommonService } from '../services/common.service';

export interface CommonStoreInitState {
  categoryList: AmmIconCategory[];
  allCategories: AmmDataLabelValue[];
  allAddresses: Address[];
  buyerInfo: UseraccountModel | undefined;
  allStates: CountryStatesMap[];
  allCountries: CountryNameMap | undefined;
  allCancelReasons: AmmDataLabelValue[];
}

export const commonStoreInitState: CommonStoreInitState = {
  categoryList: [],
  allCategories: [],
  allAddresses: [],
  buyerInfo: undefined,
  allStates: [],
  allCountries: undefined,
  allCancelReasons: [],
};

export const CommonStore = signalStore(
  { providedIn: 'root' },
  withState(commonStoreInitState),
  withComputed((store) => ({
    defaultShippingAddress: computed(() => {
      const allAddresses = store.allAddresses();
      const defaultAddress = store.buyerInfo()?.defaultDeliveryAddress;

      return defaultAddress ? defaultAddress : allAddresses[0];
    }),
    sortedAllAddresses: computed(() => {
      const allAddresses = store.allAddresses();
      const defaultAddress = store.buyerInfo()?.defaultDeliveryAddress;

      if (defaultAddress && allAddresses.length > 0) {
        const restAddresses = allAddresses.filter(
          (x) => x.id != defaultAddress.id
        );
        return [defaultAddress, ...restAddresses];
      } else {
        return allAddresses;
      }
    }),
  })),
  withMethods(
    (
      store,
      commonSrv = inject(CommonService),
      shoppingCartSrv = inject(ShoppingCartService),
      globalData = inject(GlobaldataService),
      productSrv = inject(ProductService)
    ) => ({
      loadAllCategory() {
        commonSrv.getAllCategories().subscribe((cates: string[]) => {
          if (cates) {
            const formattedCate = cates.map((c) => {
              const category = c.split('|');
              return <AmmDataLabelValue>{
                label: category[1],
                value: category[0],
              };
            });

            patchState(store, {
              allCategories: formattedCate,
            });
          }
        });
      },
      loadCategoryList() {
        productSrv.fetchCurCategories().subscribe((response: PagingData<AmmIconCategory>) => {
          if (response && response.dataList) {
            patchState(store, {
              categoryList: response.dataList
            })
          }
        })
      },
      loadAllAddresses() {
        shoppingCartSrv
          .getAllAddress()
          .subscribe((response: PagingData<Address>) => {
            if (response && response.dataList) {
              const addressesFromDb = response.dataList;
              patchState(store, {
                allAddresses: addressesFromDb,
              });
            }
          });
      },
      loadBuyerInfo() {
        shoppingCartSrv.getBuyerInfo().subscribe((user: UseraccountModel) => {
          patchState(store, {
            buyerInfo: user,
          });
        });
      },
      loadStatesByCountry(countryCode: string) {
        globalData
          .fetchCityStatesByCountryName(countryCode)
          .subscribe((response: PagingData<CityStateZip>) => {
            if (response && response.dataList && response.dataList.length > 0) {
              const statesFromDb = response.dataList;
              const states = statesFromDb.map(
                (x) => <State>{ code: x.state, name: x.stateFullName }
              );
              if (
                store.allStates()?.find((x) => x.countryCode === countryCode)
              ) {
                patchState(store, {
                  allStates: store
                    .allStates()
                    .map((x) =>
                      x.countryCode === countryCode
                        ? { ...x, states: states }
                        : x
                    ),
                });
              } else {
                patchState(store, {
                  allStates: [
                    ...store.allStates(),
                    { countryCode: countryCode, states: states },
                  ],
                });
              }
            }
          });
      },
      loadAllCountries() {
        globalData
          .fetchCountries()
          .subscribe((response: PagingData<CountryNameMap>) => {
            if (response && response.dataList) {
              patchState(store, {
                allCountries: response.dataList[0],
              });
            }
          });
      },
      loadAllCancelReasons() {
        commonSrv
          .getAllCancelReason()
          .subscribe((reasons: AmmDataLabelValue[]) => {
            if (reasons) {
              patchState(store, {
                allCancelReasons: reasons,
              });
            }
          });
      },
    })
  )
);
